import { Button } from '@/components/ui/button'
import { usePositionStorage } from '@/hooks'
import { useAuth } from '@/lib'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { OnboardingLayout } from './onboarding-layout'
import { OnboardingProgress } from './onboarding-progress'
import { OnboardingSteps } from './onboarding-steps'
import { BillingScreen } from './screens/billing-screen'
import { CompanyScreen } from './screens/company-screen'
import { ContactsCandidatesScreen } from './screens/contacts-candidates-screen'
import { FreeTrialScreen } from './screens/free-trial-screen'
import { LanguageScreen } from './screens/language-screen'
import { ManagesTeamScreen } from './screens/manages-team-screen'
import { OrganisationHeadcountScreen } from './screens/organisation-headcount-screen'
import { PhoneNumberScreen } from './screens/phone-number-screen'
import { RecruitingVolumeScreen } from './screens/recruiting-volume-screen'
import { useOnboardingFlow } from './use-onboarding-flow'

export function OnboardingPage() {
	const {
		currentStep,
		furthestStep,
		steps,
		trialSetup,
		isLoading,
		hasFailed,
		submit,
		saveCompany,
		continueSetup,
		finish,
		goTo,
		isReachable,
		goBack,
		canGoBack,
		goForward,
		canGoForward,
		isSubmitting,
	} = useOnboardingFlow()
	const { user } = useAuth()
	const { getCompanyUrl } = usePositionStorage()
	const queryClient = useQueryClient()
	const { t } = useTranslation()
	const [selectedCardId, setSelectedCardId] = useState<string | null>(null)

	const organisationSlug = trialSetup.organisation?.slug ?? null

	useEffect(() => {
		if (!user?.onboardingCompletedAt) { return }
		void queryClient.cancelQueries().finally(() => {
			window.location.assign(organisationSlug ? getCompanyUrl(organisationSlug) : '/company')
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.onboardingCompletedAt, queryClient])

	if (isLoading || currentStep === 'complete') {
		return (
			<OnboardingLayout>
				{currentStep === 'complete' && hasFailed
					? (
						<div className="flex flex-col items-center gap-4 py-16">
							<p role="alert" className="text-center text-sm text-destructive">
								{t('onboarding.failed')}
							</p>
							<Button variant="outline" onClick={finish}>
								{t('common.tryAgain')}
							</Button>
						</div>
					)
					: (
						<div className="flex items-center justify-center py-16 text-muted-foreground">
							<Loader2 className="size-6 animate-spin" />
						</div>
					)}
			</OnboardingLayout>
		)
	}

	const { organisation, profile, cards } = trialSetup
	const selectedCard = cards.find((card) => card.id === selectedCardId) ?? cards[0] ?? null

	const renderScreen = () => {
		switch (currentStep) {
			case 'language':
				return <LanguageScreen isSubmitting={isSubmitting} onSubmit={(v) => submit('language', v)} />
			case 'company':
				return (
					<CompanyScreen
						currentName={trialSetup.organisation?.name ?? null}
						isSubmitting={isSubmitting}
						onSubmit={saveCompany}
					/>
				)
			case 'organisation_headcount':
				return (
					<OrganisationHeadcountScreen
						current={user?.organisationHeadcount ?? null}
						isSubmitting={isSubmitting}
						onSubmit={(v) => submit('organisationHeadcount', v)}
					/>
				)
			case 'manages_team':
				return <ManagesTeamScreen isSubmitting={isSubmitting} onSubmit={(v) => submit('managesTeam', v)} />
			case 'contacts_candidates':
				return (
					<ContactsCandidatesScreen
						isSubmitting={isSubmitting}
						onSubmit={(v) => submit('contactsCandidates', v)}
					/>
				)
			case 'recruiting_volume':
				return (
					<RecruitingVolumeScreen
						isSubmitting={isSubmitting}
						onSubmit={(v) => submit('recruitingVolume', v)}
					/>
				)
			case 'phone_number':
				return (
					<PhoneNumberScreen
						current={user?.phoneNumber ?? null}
						submitLabel={user?.isSelfSignup ? t('onboarding.continue') : t('onboarding.phoneNumber.submit')}
						isSubmitting={isSubmitting}
						onSubmit={(v) => submit('phoneNumber', v)}
					/>
				)
			case 'billing':
				return organisation && (
					<BillingScreen
						organisationId={organisation.id}
						profile={profile}
						selectedCardId={selectedCard?.id ?? null}
						onSelectCard={setSelectedCardId}
						onContinue={continueSetup}
					/>
				)
			case 'free_trial':
				return organisation && profile && selectedCard && (
					<FreeTrialScreen
						organisationId={organisation.id}
						profile={profile}
						card={selectedCard}
						onStarted={finish}
					/>
				)
		}
	}

	const progressProps = {
		step: currentStep,
		steps,
		furthestStep,
		isReachable,
		onSelect: goTo,
		disabled: isSubmitting,
	}
	const canSkip = !user?.isSelfSignup

	return (
		<OnboardingLayout aside={<OnboardingSteps {...progressProps} />}>
			<div className="flex flex-col gap-8">
				<div className="lg:hidden">
					<OnboardingProgress {...progressProps} />
				</div>
				{hasFailed && (
					<p role="alert" className="text-center text-sm text-destructive">
						{t('onboarding.failed')}
					</p>
				)}
				<AnimatePresence mode="wait" initial={false}>
					<motion.div
						key={currentStep}
						initial={{ opacity: 0, x: 24 }}
						animate={{ opacity: 1, x: 0 }}
						exit={{ opacity: 0, x: -24 }}
						transition={{ duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
						className="flex flex-col gap-8"
					>
						{renderScreen()}
					</motion.div>
				</AnimatePresence>
				{(canGoBack || canGoForward || canSkip) && (
					<div className="-mb-2 flex items-center gap-2 border-t border-brand-50 pt-4">
						{canGoBack && (
							<Button
								variant="ghost"
								size="sm"
								onClick={goBack}
								disabled={isSubmitting}
								className="-ml-3 text-muted-foreground hover:text-foreground"
							>
								<ArrowLeft className="size-4" />
								{t('common.back')}
							</Button>
						)}
						<div className="ml-auto flex items-center gap-2">
							{/* Self-signups are sales leads and start a trial, so they go through every step. Invited users may skip. */}
							{canSkip && (
								<Button
									variant="ghost"
									size="sm"
									onClick={finish}
									disabled={isSubmitting}
									className="text-muted-foreground hover:text-foreground"
								>
									{t('onboarding.skip')}
								</Button>
							)}
							{canGoForward && (
								<Button
									variant="ghost"
									size="sm"
									onClick={goForward}
									disabled={isSubmitting}
									className="-mr-3 text-muted-foreground hover:text-foreground"
								>
									{t('onboarding.progress.next')}
									<ArrowRight className="size-4" />
								</Button>
							)}
						</div>
					</div>
				)}
			</div>
		</OnboardingLayout>
	)
}
